import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserSubmission, RegularUser, UserProfile, SubmittedReport, SubmittedSurvey } from '../types/admin';
import { UserProfileEdit } from './UserProfileEdit';
import { PasswordChangeModal } from './PasswordChangeModal';
import { Logo } from './Logo';
import { getUserSubmissions, updateUserProfile, changeUserPassword, PasswordChangeRequest } from '../services/userService';
import './UserDashboard.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRiskLevelClass, getRiskLevelText, getTargetGroupText } from '../utils/surveyUtils';
import { ReportViewModal } from './ReportViewModal';
import { SurveyViewModal } from './SurveyViewModal';

export const UserDashboard: React.FC = () => {
	const { user, logout, token } = useAuth();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const reportId = searchParams.get('reportId');

	const [submissions, setSubmissions] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [showProfileEdit, setShowProfileEdit] = useState(false);
	const [showPasswordChange, setShowPasswordChange] = useState(false);
	const [selectedReport, setSelectedReport] = useState<SubmittedReport | null>(null);
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [selectedSurvey, setSelectedSurvey] = useState<SubmittedSurvey | null>(null);
	const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

	// Cast user to RegularUser since we know it's not admin in this component
	const regularUser = user as RegularUser;

	const loadUserSubmissions = useCallback(async () => {
		try {
			setIsLoading(true);
			if (token) {
				const userSubmissions = await getUserSubmissions(token);
				setSubmissions(userSubmissions);
				if (reportId) {
					const reportToOpen = userSubmissions.find((submission) => submission.id === reportId);
					if (reportToOpen) {
						setSelectedReport(reportToOpen as SubmittedReport);
						setIsReportModalOpen(true);
					}
				}
			} else {
				// Fallback to mock data if no token
				const mockSubmissions: UserSubmission[] = [
					{
						id: '1',
						surveyId: 'survey-1',
						riskScore: 0.65,
						submittedAt: '2024-01-15T10:30:00Z',
						formData: {},
						type: 'survey',
						status: 'reviewed',
						riskLevel: 'medium',
						title: 'Prijava sumnje na trgovinu decom',
						description: 'Nivo rizika: 65.0%',
					},
					{
						id: '2',
						surveyId: 'survey-2',
						riskScore: 0.85,
						submittedAt: '2024-01-20T14:15:00Z',
						formData: {},
						type: 'survey',
						status: 'pending',
						riskLevel: 'high',
						title: 'Hitna prijava trgovine ljudima',
						description: 'Nivo rizika: 85.0%',
					},
					{
						id: '3',
						surveyId: 'survey-3',
						riskScore: 0.25,
						submittedAt: '2024-01-25T09:45:00Z',
						formData: {},
						type: 'survey',
						status: 'processed',
						riskLevel: 'low',
						title: 'Rutinska provera',
						description: 'Nivo rizika: 25.0%',
					},
				];
				setSubmissions(mockSubmissions);
			}
		} catch (error) {
			console.error('Error loading submissions:', error);
			// Fallback to empty array
			setSubmissions([]);
		} finally {
			setIsLoading(false);
		}
	}, [token]);

	useEffect(() => {
		// Load user submissions
		loadUserSubmissions();
	}, [loadUserSubmissions]);

	const handleViewSubmission = (submissionId: string) => {
		// TODO: Implement navigation to submission detail view
		console.log('Viewing submission:', submissionId);
	};

	const handleNewSubmission = () => {
		// Navigate to survey component
		navigate('/survey');
	};

	const handleSaveProfile = async (profile: UserProfile) => {
		if (token) {
			await updateUserProfile(token, profile);
		} else {
			// Mock implementation
			console.log('Saving profile:', profile);
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
	};

	const handlePasswordChange = async (passwordData: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
		if (token) {
			const requestData: PasswordChangeRequest = {
				currentPassword: passwordData.currentPassword,
				newPassword: passwordData.newPassword,
			};
			await changeUserPassword(token, requestData);
		} else {
			// Mock implementation
			console.log('Changing password:', passwordData);
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('sr-RS', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};
	const handleViewReport = (report: SubmittedReport) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set('reportId', report.id);
		setSearchParams(newParams);
		setSelectedReport(report);
		setIsReportModalOpen(true);
	};

	const handleCloseReportModal = () => {
		const newParams = new URLSearchParams(searchParams);
		newParams.delete('reportId');
		setSearchParams(newParams);
		setIsReportModalOpen(false);
		setSelectedReport(null);
	};

	const handleViewSurvey = (e: React.MouseEvent, report: any) => {
		e.stopPropagation();
		if (report.survey) {
			setSelectedSurvey(report.survey);
			setIsSurveyModalOpen(true);
		}
	};

	const handleCloseSurveyModal = () => {
		setIsSurveyModalOpen(false);
		setSelectedSurvey(null);
	};

	const getReportStatusText = (decision: string | null) => {
		if (decision == null) return 'Na čekanju';
		return decision ? 'Prihvaćen' : 'Odbijen';
	};

	return (
		<div className='user-dashboard'>
			<header className='dashboard-header'>
				<div className='header-content'>
					<Logo size='small' />
					<div className='nav-user-info'>
						<h1>
							Dobrodošli, {regularUser.firstName} {regularUser.lastName}
						</h1>
						<p className='user-details'>
							{!!regularUser?.institution ? regularUser.institution : 'Nema naziva ustanove'} • {regularUser.email}
						</p>
					</div>
					<div className='header-actions'>
						<button className='btn btn-secondary' onClick={() => setShowProfileEdit(true)}>
							Izmeni podatke
						</button>
						<button className='btn btn-secondary' onClick={() => setShowPasswordChange(true)}>
							Promeni lozinku
						</button>
						<button className='btn btn-outline' onClick={logout}>
							Odjavi se
						</button>
					</div>
				</div>
			</header>

			<main className='dashboard-main'>
				<section className='submissions-section'>
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
						<h2>Vaše prijave</h2>
						<button className='btn btn-primary btn-large' onClick={handleNewSubmission}>
							Nova prijava
						</button>
					</div>

					{isLoading ? (
						<div className='loading'>Učitavanje...</div>
					) : submissions.length === 0 ? (
						<div className='empty-state'>
							<p>Još uvek nemate podnetih prijava.</p>
							<button className='btn btn-primary' onClick={handleNewSubmission}>
								Podnesi prvu prijavu
							</button>
						</div>
					) : (
						<div className='submissions-list'>
							{submissions.map((report) => (
								<div key={report.id} className='submission-card' onClick={() => handleViewReport(report)}>
									<div className='submission-details'>
										<div className='detail-item'>
											<span className='label'>Žrtva:</span>
											<span className='value' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{report.formData.name} {report.formData.surname}
											</span>
										</div>
										<div className='detail-item'>
											<span className='label'>Datum:</span>
											<span className='value' style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
												{formatDate(report.submittedAt)}
											</span>
										</div>

										{/* {user?.type !== 'health' && ( */}
										<div className='detail-item'>
											<span className='label'>Status:</span>
											<span className='value'>{getReportStatusText(report.decision)}</span>
										</div>
										{/* )} */}
										<div className='detail-item'>
											<span className='label'>Rizik:</span>
											{report.survey && <span className={`value  risk-badge ${getRiskLevelClass(report.survey.riskLevel)}`}>{getRiskLevelText(report.survey.riskLevel)}</span>}
										</div>

										{/* <div className='submission-actions'>
											<button onClick={(e) => handleViewSurvey(e, report)} className='btn btn-primary btn-sm'>
												Prikaži upitnik
											</button>
										</div> */}
									</div>
								</div>
							))}
						</div>
					)}
				</section>
			</main>

			{/* Profile edit modal */}
			<UserProfileEdit isOpen={showProfileEdit} onClose={() => setShowProfileEdit(false)} onSave={handleSaveProfile} />

			{/* Password change modal */}
			<PasswordChangeModal isOpen={showPasswordChange} onClose={() => setShowPasswordChange(false)} onSave={handlePasswordChange} />

			{/* Report view modal */}
			{selectedReport && <ReportViewModal isOpen={isReportModalOpen} onClose={handleCloseReportModal} reportData={selectedReport.formData} report={selectedReport} surveyId={selectedReport.surveyId} riskScore={selectedReport.riskScore} timestamp={selectedReport.submittedAt} />}

			{/* Survey view modal */}
			{selectedSurvey && <SurveyViewModal isOpen={isSurveyModalOpen} onClose={handleCloseSurveyModal} surveyData={selectedSurvey} />}
		</div>
	);
};
